import React, { useState } from 'react';
import { PlayIcon, PauseIcon, FlagIcon, CheckIcon, PencilIcon, AlertTriangleIcon, CircleCheckIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { Header } from '../components/Header';
import { copy } from '../copy';
import { ConfidenceMatrix } from '../components/discovery/ConfidenceMatrix';
const THRESHOLD = copy.review.confidenceThreshold;
function segShade(confidence: number): string {
  if (confidence < 40) return 'border-l-red-500 bg-red-50/40 dark:bg-red-900/10';
  if (confidence < 70) return 'border-l-amber-500 bg-amber-50/40 dark:bg-amber-900/10';
  return 'border-l-emerald-500 bg-emerald-50/30 dark:bg-emerald-900/10';
}
export function WorkbenchPage() {
  const w = copy.workbench;
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [verified, setVerified] = useState(false);
  const [activeSeg, setActiveSeg] = useState<string | null>(null);
  // Waveform bars with flagged-segment markers.
  const bars = Array.from({
    length: 64
  }, (_, i) => 20 + i * 37 % 60);
  const flaggedMarks = [18, 30]; // bar indices with low-confidence segments
  return <div className="min-h-screen bg-white dark:bg-[#1a1a1a] text-slate-900 dark:text-[#e5e5e5] transition-colors duration-200" data-id="element-571">
      <Header title={w.headerTitle} minimal showBackArrow backTo="/case/files" data-id="element-572" />

      <main className="mx-auto max-w-[1400px] px-6 py-8" data-id="element-573">
        <div className="flex items-center justify-between mb-6" data-id="element-574">
          <div data-id="element-575">
            <h1 className="text-xl font-bold text-slate-900 dark:text-white" data-id="element-576">
              {w.fileName}
            </h1>
            <p className="text-xs text-slate-500 dark:text-[#a3a3a3]" data-id="element-577">
              {w.subfolder}
            </p>
          </div>
          <button onClick={() => setVerified(v => !v)} className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border transition-all ${verified ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800' : 'border-slate-200 dark:border-[#333] text-slate-600 dark:text-[#a3a3a3] hover:bg-slate-50 dark:hover:bg-white/5'}`} data-id="element-578">
            <CircleCheckIcon size={16} data-id="element-579" />
            {w.verifiedToggle}
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-id="element-580">
          {/* Media pane */}
          <section className="lg:col-span-4 space-y-4" data-id="element-581">
            <h2 className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-[#555]" data-id="element-582">
              {w.mediaTitle}
            </h2>
            <div className="rounded-2xl border border-slate-200 dark:border-[#333] bg-slate-900 dark:bg-black aspect-video flex items-center justify-center text-slate-500" data-id="element-583">
              <PlayIcon size={40} className="opacity-40" data-id="element-584" />
            </div>
            <div className="rounded-2xl border border-slate-200 dark:border-[#333] bg-slate-50 dark:bg-[#222] p-4 space-y-3" data-id="element-585">
              <div className="relative flex items-end gap-0.5 h-16 cursor-pointer" role="slider" aria-label="Seek" aria-valuenow={position} tabIndex={0} onClick={e => {
              const rect = e.currentTarget.getBoundingClientRect();
              setPosition(Math.round((e.clientX - rect.left) / rect.width * 100));
            }} data-id="element-586">
                {bars.map((h, i) => {
                const isFlag = flaggedMarks.includes(i);
                const played = i / bars.length * 100 <= position;
                return <div key={i} style={{
                  height: `${h}%`
                }} className={`flex-1 rounded-sm ${isFlag ? 'bg-red-500' : played ? 'bg-blue-500' : 'bg-slate-300 dark:bg-[#444]'}`} data-id="element-587" />;
              })}
              </div>
              <div className="flex items-center gap-3" data-id="element-588">
                <button onClick={() => setPlaying(p => !p)} className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-900 dark:bg-white text-white dark:text-slate-900" data-id="element-589">
                  {playing ? <PauseIcon size={16} data-id="element-590" /> : <PlayIcon size={16} data-id="element-591" />}
                </button>
                <span className="text-xs font-mono text-slate-500 dark:text-[#a3a3a3]" data-id="element-592">
                  {String(Math.floor(position / 100 * 15)).padStart(2, '0')}:
                  {String(Math.floor(position / 100 * 60) % 60).padStart(2, '0')}{' '}
                  / 15:20
                </span>
                <span className="ml-auto flex items-center gap-1 text-[10px] text-red-500" data-id="element-593">
                  <FlagIcon size={11} data-id="element-594" />
                  {flaggedMarks.length} flagged
                </span>
              </div>
            </div>
          </section>

          {/* Transcript pane */}
          <section className="lg:col-span-5 space-y-4" data-id="element-595">
            <div className="flex items-center justify-between" data-id="element-596">
              <h2 className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-[#555]" data-id="element-597">
                {w.transcriptTitle}
              </h2>
              <button className="flex items-center gap-1 text-xs text-slate-500 dark:text-[#a3a3a3] hover:text-slate-700 dark:hover:text-white" data-id="element-598">
                <PencilIcon size={12} data-id="element-599" />
                {w.addSpeakerLabel}
              </button>
            </div>
            <div className="space-y-2" data-id="element-600">
              {w.segments.map(seg => {
              const low = seg.confidence < THRESHOLD;
              return <motion.div key={seg.id} initial={{
                opacity: 0,
                y: 4
              }} animate={{
                opacity: 1,
                y: 0
              }} className={`rounded-xl border border-slate-200 dark:border-[#333] border-l-4 p-3 ${segShade(seg.confidence)} ${activeSeg === seg.id ? 'ring-2 ring-blue-400' : ''}`} data-id="element-601">
                    <div className="flex items-center justify-between mb-1.5" data-id="element-602">
                      <button onClick={() => {
                    setActiveSeg(seg.id);
                    setPosition(parseInt(seg.start.split(':')[1], 10) * 5);
                  }} className="flex items-center gap-2 text-xs font-mono text-blue-600 dark:text-blue-400 hover:underline" title="Jump player to this point" data-id="element-603">
                        {seg.start}
                        {low && <AlertTriangleIcon size={12} className="text-red-500" data-id="element-604" />}
                      </button>
                      <input defaultValue={seg.speaker} aria-label="Speaker label" className="bg-transparent text-right text-xs font-semibold text-slate-700 dark:text-[#e5e5e5] outline-none focus:underline w-32" data-id="element-605" />
                    </div>
                    <p className="text-sm text-slate-700 dark:text-[#e5e5e5] leading-relaxed" data-id="element-606">
                      {seg.text}
                    </p>
                    <div className="mt-2 flex items-center gap-1" data-id="element-607">
                      <button className="flex items-center gap-1 rounded-md px-2 py-1 text-[11px] text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20" data-id="element-608">
                        <CheckIcon size={12} data-id="element-609" />
                        {w.segmentActions.accept}
                      </button>
                      <button className="flex items-center gap-1 rounded-md px-2 py-1 text-[11px] text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20" data-id="element-610">
                        <PencilIcon size={12} data-id="element-611" />
                        {w.segmentActions.correct}
                      </button>
                      <button className="flex items-center gap-1 rounded-md px-2 py-1 text-[11px] text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20" data-id="element-612">
                        <FlagIcon size={12} data-id="element-613" />
                        {w.segmentActions.dispute}
                      </button>
                    </div>
                  </motion.div>;
            })}
            </div>
          </section>

          {/* Confidence Matrix pane */}
          <section className="lg:col-span-3 space-y-4" data-id="element-614">
            <div data-id="element-615">
              <h2 className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-[#555]" data-id="element-616">
                {w.matrixTitle}
              </h2>
              <p className="text-xs text-slate-400 dark:text-[#555] mt-1" data-id="element-617">
                {w.matrixSubtitle}
              </p>
            </div>
            <ConfidenceMatrix data-id="element-618" />
          </section>
        </div>
      </main>
    </div>;
}